// src/pages/document/DocumentCreatePage.tsx
// Two distinct creation flows:
// 1. Wiki Page — minimal, instant (like Notion: pick template → title → go)
// 2. Controlled Document — formal form with type, department, confidentiality, numbering

import { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useDocument, useI18n } from '@react/hooks';
import { useScope } from '@/hooks/useScope';
import { ErrorAlert } from '@react/components';
import {
  documentTypeMap,
  documentConfidentialityMap,
  getStatusMapping,
} from '@/features/document/logic';
import { useDocumentContext } from '@/hooks/useDocumentContext';
import { Select } from '@/components/Select';
import { TitleInput, AutoGrowTextarea, FormRow } from '@/components/form';

// ---------------------------------------------------------------------------
// SVG icons
// ---------------------------------------------------------------------------

const icons = {
  blank: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
      <path d="M14 2v6h6" />
    </svg>
  ),
  meeting: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
      <path d="M14 2v6h6" /><path d="M8 13h8" /><path d="M8 17h8" /><path d="M8 9h2" />
    </svg>
  ),
  procedure: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M8 7h8" /><path d="M8 11h8" /><path d="M8 15h5" />
      <path d="M3 7h2" /><path d="M3 11h2" /><path d="M3 15h2" />
    </svg>
  ),
  policy: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
    </svg>
  ),
  report: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
      <path d="M3 3v18h18" /><path d="M7 16l4-8 4 4 4-6" />
    </svg>
  ),
  guideline: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
      <path d="M9 18h6" /><path d="M10 22h4" />
      <path d="M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2Z" />
    </svg>
  ),
  plan: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2Z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7Z" />
    </svg>
  ),
  spec: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
      <circle cx="12" cy="12" r="3" />
      <path d="M12 1v2" /><path d="M12 21v2" />
      <path d="M4.22 4.22l1.42 1.42" /><path d="M18.36 18.36l1.42 1.42" />
      <path d="M1 12h2" /><path d="M21 12h2" />
      <path d="M4.22 19.78l1.42-1.42" /><path d="M18.36 5.64l1.42-1.42" />
    </svg>
  ),
} as const;

// ---------------------------------------------------------------------------
// Mode picker — first screen
// ---------------------------------------------------------------------------

function ModePicker({
  onSelectMode,
  t,
}: {
  onSelectMode: (mode: 'page' | 'controlled') => void;
  t: (k: string) => string;
}) {
  return (
    <div className="text-center">
      {/* The tab already names this page; the heading stays for screen readers, the prompt below still guides the choice. */}
      <h1 className="sr-only">{t('document.newPage')}</h1>
      <p className="text-muted-foreground mb-10">{t('document.createChooseMode')}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl mx-auto">
        {/* Wiki page */}
        <button
          onClick={() => onSelectMode('page')}
          className="group flex flex-col items-center p-6 rounded-xl border-2 border-border hover:border-primary/40 hover:shadow-md transition-all text-center"
        >
          <div className="mb-4 p-3 rounded-full bg-primary/10 text-primary group-hover:bg-primary/20 transition-colors">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-8 h-8">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M14 2v6h6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-1">{t('document.createMode.page')}</h3>
          <p className="text-sm text-muted-foreground">{t('document.createMode.pageDesc')}</p>
        </button>

        {/* Controlled document */}
        <button
          onClick={() => onSelectMode('controlled')}
          className="group flex flex-col items-center p-6 rounded-xl border-2 border-border hover:border-primary/40 hover:shadow-md transition-all text-center"
        >
          <div className="mb-4 p-3 rounded-full bg-primary/10 text-primary group-hover:bg-primary/20 transition-colors">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-8 h-8">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-1">{t('document.createMode.controlled')}</h3>
          <p className="text-sm text-muted-foreground">{t('document.createMode.controlledDesc')}</p>
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Template card
// ---------------------------------------------------------------------------

interface TemplateOption {
  id: string;
  titleKey: string;
  descKey: string;
  icon: React.ReactNode;
  type: string;
}

const PAGE_TEMPLATES: TemplateOption[] = [
  { id: 'blank', titleKey: 'document.tplBlankTitle', descKey: 'document.tplBlankDesc', icon: icons.blank, type: 'DOCUMENT_TYPE_OTHER' },
  { id: 'meeting', titleKey: 'document.tplMeetingTitle', descKey: 'document.tplMeetingDesc', icon: icons.meeting, type: 'DOCUMENT_TYPE_MEMO' },
  { id: 'guideline', titleKey: 'document.tplGuidelineTitle', descKey: 'document.tplGuidelineDesc', icon: icons.guideline, type: 'DOCUMENT_TYPE_GUIDELINE' },
  { id: 'plan', titleKey: 'document.tplPlanTitle', descKey: 'document.tplPlanDesc', icon: icons.plan, type: 'DOCUMENT_TYPE_PLAN' },
  { id: 'report', titleKey: 'document.tplReportTitle', descKey: 'document.tplReportDesc', icon: icons.report, type: 'DOCUMENT_TYPE_REPORT' },
];

function TemplateCard({
  template,
  onClick,
  isCreating,
}: {
  template: TemplateOption;
  onClick: () => void;
  isCreating: boolean;
}) {
  const { t } = useI18n();
  return (
    <button
      onClick={onClick}
      disabled={isCreating}
      className="group flex flex-col items-start p-4 rounded-xl border border-border hover:border-primary/30 hover:shadow-sm transition-all text-left disabled:opacity-50"
    >
      <div className="mb-3 text-muted-foreground group-hover:text-primary transition-colors">
        {template.icon}
      </div>
      <h3 className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
        {t(template.titleKey)}
      </h3>
      <p className="text-xs text-muted-foreground mt-0.5">{t(template.descKey)}</p>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Page (Wiki) creation — template picker then title
// ---------------------------------------------------------------------------

function PageCreationFlow({
  onBack,
  onSubmit,
  isSubmitting,
  error,
  onDismissError,
  initialTitle,
}: {
  onBack: () => void;
  onSubmit: (title: string, type: string) => void;
  isSubmitting: boolean;
  error: unknown;
  onDismissError: () => void;
  initialTitle?: string;
}) {
  const { t } = useI18n();
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateOption | null>(
    initialTitle ? PAGE_TEMPLATES[0] : null,
  );
  const [title, setTitle] = useState(initialTitle || '');

  if (!selectedTemplate) {
    return (
      <>
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
            <path d="M19 12H5" strokeLinecap="round" /><path d="M12 19l-7-7 7-7" strokeLinecap="round" />
          </svg>
          {t('common.back')}
        </button>

        <h2 className="text-2xl font-bold text-foreground mb-2">{t('document.createMode.page')}</h2>
        <p className="text-muted-foreground mb-6">{t('document.chooseTemplate')}</p>

        {!!error && <ErrorAlert error={error} dismissible onDismiss={onDismissError} />}

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {PAGE_TEMPLATES.map((tpl) => (
            <TemplateCard
              key={tpl.id}
              template={tpl}
              onClick={() => setSelectedTemplate(tpl)}
              isCreating={false}
            />
          ))}
        </div>

        <div className="mt-8 pt-6 border-t border-border">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">{t('document.templates')}</h3>
            <Link to="/documents/templates" className="text-sm text-primary hover:underline">
              {t('document.viewAll')}
            </Link>
          </div>
          <p className="text-sm text-muted-foreground">{t('document.templatesHint')}</p>
        </div>
      </>
    );
  }

  // Title input
  const submit = () => {
    if (!title.trim()) return;
    onSubmit(title.trim(), selectedTemplate.type);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submit();
  };

  return (
    <>
      <button
        onClick={() => setSelectedTemplate(null)}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
          <path d="M19 12H5" strokeLinecap="round" /><path d="M12 19l-7-7 7-7" strokeLinecap="round" />
        </svg>
        {t('document.chooseTemplate')}
      </button>

      {!!error && <ErrorAlert error={error} dismissible onDismiss={onDismissError} />}

      <form onSubmit={handleSubmit} className="max-w-lg mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <div className="text-primary">{selectedTemplate.icon}</div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">{t(selectedTemplate.titleKey)}</h2>
            <p className="text-sm text-muted-foreground">{t(selectedTemplate.descKey)}</p>
          </div>
        </div>

        <div className="mb-6">
          <TitleInput
            id="page-title"
            value={title}
            onChange={setTitle}
            placeholder={t('document.fieldTitlePlaceholder')}
            initialFocus
            onEnter={submit}
            className="text-xl"
          />
        </div>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={isSubmitting || !title.trim()}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {isSubmitting ? t('common.saving') : t('document.create')}
          </button>
        </div>
      </form>
    </>
  );
}

// ---------------------------------------------------------------------------
// Controlled document creation — full form
// ---------------------------------------------------------------------------

const CONTROLLED_DOC_TYPES = [
  'DOCUMENT_TYPE_POLICY',
  'DOCUMENT_TYPE_MANUAL',
  'DOCUMENT_TYPE_PROCEDURE',
  'DOCUMENT_TYPE_WORK_INSTRUCTION',
  'DOCUMENT_TYPE_FORM',
  'DOCUMENT_TYPE_RECORD',
  'DOCUMENT_TYPE_SPECIFICATION',
  'DOCUMENT_TYPE_CONTRACT',
  'DOCUMENT_TYPE_REPORT',
];

const CONFIDENTIALITY_OPTIONS = [
  'DOCUMENT_CONFIDENTIALITY_PUBLIC',
  'DOCUMENT_CONFIDENTIALITY_INTERNAL',
  'DOCUMENT_CONFIDENTIALITY_RESTRICTED',
  'DOCUMENT_CONFIDENTIALITY_CONFIDENTIAL',
  'DOCUMENT_CONFIDENTIALITY_SECRET',
];

function ControlledCreationFlow({
  onBack,
  onSubmit,
  isSubmitting,
  error,
  onDismissError,
}: {
  onBack: () => void;
  onSubmit: (data: {
    title: string;
    description: string;
    type: string;
    department: string;
    confidentiality: string;
  }) => void;
  isSubmitting: boolean;
  error: unknown;
  onDismissError: () => void;
}) {
  const { t } = useI18n();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [docType, setDocType] = useState(CONTROLLED_DOC_TYPES[0]);
  const [department, setDepartment] = useState('');
  const [confidentiality, setConfidentiality] = useState(CONFIDENTIALITY_OPTIONS[1]);
  const descRef = useRef<HTMLTextAreaElement>(null);

  const canSubmit = !!title.trim();

  const submit = () => {
    if (!canSubmit) return;
    onSubmit({
      title: title.trim(),
      description: description.trim(),
      type: docType,
      department: department.trim(),
      confidentiality,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submit();
  };

  return (
    <>
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
          <path d="M19 12H5" strokeLinecap="round" /><path d="M12 19l-7-7 7-7" strokeLinecap="round" />
        </svg>
        {t('common.back')}
      </button>

      <div className="flex items-center gap-3 mb-8">
        <div className="p-2.5 rounded-full bg-primary/10 text-primary">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-6 h-6">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <div>
          <h2 className="text-xl font-bold text-foreground">{t('document.createMode.controlled')}</h2>
          <p className="text-sm text-muted-foreground">{t('document.createMode.controlledHint')}</p>
        </div>
      </div>

      {!!error && <ErrorAlert error={error} dismissible onDismiss={onDismissError} />}

      <form onSubmit={handleSubmit} className="max-w-2xl">
        {/* Title + description — ghost fields, the page is the box */}
        <TitleInput
          id="ctrl-title"
          value={title}
          onChange={setTitle}
          placeholder={t('document.fieldTitlePlaceholder')}
          initialFocus
          onEnter={() => descRef.current?.focus()}
          className="text-2xl"
        />
        <AutoGrowTextarea
          ref={descRef}
          id="ctrl-desc"
          value={description}
          onChange={setDescription}
          placeholder={t('document.fieldDescriptionPlaceholder')}
          className="mb-2"
        />

        {/* Properties */}
        <div className="border-t border-border pt-3 mt-3">
          <FormRow label={t('document.fieldType')} labelWidth="sm" htmlFor="ctrl-type">
            <Select id="ctrl-type" value={docType} onChange={(e) => setDocType(e.target.value)} disabled={isSubmitting} fullWidth={false} className="w-64">
              {CONTROLLED_DOC_TYPES.map((type) => {
                const info = getStatusMapping(documentTypeMap, type);
                return (
                  <option key={type} value={type}>
                    {t(info.labelKey)}
                  </option>
                );
              })}
            </Select>
          </FormRow>

          <FormRow label={t('document.confidentiality._')} labelWidth="sm" htmlFor="ctrl-conf">
            <Select
              id="ctrl-conf"
              value={confidentiality}
              onChange={(e) => setConfidentiality(e.target.value)}
              disabled={isSubmitting}
              fullWidth={false}
              className="w-64"
            >
              {CONFIDENTIALITY_OPTIONS.map((conf) => {
                const info = getStatusMapping(documentConfidentialityMap, conf);
                return (
                  <option key={conf} value={conf}>
                    {t(info.labelKey)}
                  </option>
                );
              })}
            </Select>
          </FormRow>

          <FormRow label={t('document.fieldDepartment')} labelWidth="sm" htmlFor="ctrl-dept">
            <input
              id="ctrl-dept"
              type="text"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              placeholder={t('document.fieldDepartmentPlaceholder')}
              className="w-64 px-3 py-1.5 rounded-md border border-input bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              disabled={isSubmitting}
            />
          </FormRow>
        </div>

        {/* Info box */}
        <div className="flex items-start gap-3 p-3 mt-4 rounded-lg bg-muted/50 border border-border">
          <svg className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 22a10 10 0 100-20 10 10 0 000 20zM12 16v-4M12 8h.01" />
          </svg>
          <p className="text-xs text-muted-foreground">{t('document.createMode.controlledInfo')}</p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 pt-4">
          <button
            type="submit"
            disabled={isSubmitting || !canSubmit}
            className="px-5 py-2.5 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {isSubmitting ? t('common.saving') : t('document.create')}
          </button>
          <button
            type="button"
            onClick={onBack}
            disabled={isSubmitting}
            className="px-4 py-2.5 rounded-lg border border-border text-foreground hover:bg-muted transition-colors disabled:opacity-50"
          >
            {t('common.cancel')}
          </button>
        </div>
      </form>
    </>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const DocumentCreatePage = observer(() => {
  const navigate = useNavigate();
  const store = useDocument();
  const scope = useScope();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState<'pick' | 'page' | 'controlled'>('pick');
  const [isCreating, setIsCreating] = useState(false);

  const initialTitle = searchParams.get('title') || undefined;

  // Scope-aware creation: if coming from a project/entity context
  const scopeEntityType = searchParams.get('scope_type') || undefined;
  const scopeEntityId = searchParams.get('scope_id') || undefined;
  // Pre-load document context for scope-aware creation (used for filtering types/templates)
  const { context: _docContext } = useDocumentContext(scopeEntityType, scopeEntityId);

  // ?quick → page mode, ?controlled → controlled mode, ?title → page with prefilled title
  useEffect(() => {
    if (searchParams.get('quick') === 'true' || initialTitle) {
      setMode('page');
    } else if (searchParams.get('controlled') === 'true') {
      setMode('controlled');
    }
  }, []);

  // Arriving from another entity's Documents tab (?scope_type=&scope_id=):
  // the new document belongs to that entity via `parent`.
  const parentEntity = scopeEntityType && scopeEntityId
    ? { id: scopeEntityId, type_id: scopeEntityType, type_name: scopeEntityType } as never
    : undefined;

  const handleCreatePage = useCallback(async (title: string, type: string) => {
    setIsCreating(true);
    try {
      const doc = await store.CreateDocument({
        title,
        type,
        parent: parentEntity,
      } as any);
      const newId = doc?.entity?.id;
      if (newId) navigate(`/documents/${newId}`, { replace: true });
    } catch {
      // Error displayed by store
    } finally {
      setIsCreating(false);
    }
  }, [scope.productId, store, navigate]);

  const handleCreateControlled = useCallback(async (data: {
    title: string;
    description: string;
    type: string;
    department: string;
    confidentiality: string;
  }) => {
    setIsCreating(true);
    try {
      const doc = await store.CreateDocument({
        title: data.title,
        description: data.description || undefined,
        type: data.type,
        department: data.department || undefined,
        confidentiality: data.confidentiality,
        parent: parentEntity,
      } as any);
      const newId = doc?.entity?.id;
      if (newId) navigate(`/documents/${newId}`, { replace: true });
    } catch {
      // Error displayed by store
    } finally {
      setIsCreating(false);
    }
  }, [scope.productId, store, navigate]);

  return (
    <div className="max-w-3xl mx-auto py-12 px-6">
      {/* Breadcrumb */}
      <nav className="text-sm mb-8">
        <Link to="/documents" className="text-muted-foreground hover:text-foreground transition-colors">
          {t('document.list')}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{t('document.newPage')}</span>
      </nav>

      {mode === 'pick' && (
        <ModePicker onSelectMode={(m) => setMode(m)} t={t} />
      )}

      {mode === 'page' && (
        <PageCreationFlow
          onBack={() => setMode('pick')}
          onSubmit={handleCreatePage}
          isSubmitting={isCreating}
          error={store.error}
          onDismissError={() => store.clearError()}
          initialTitle={initialTitle}
        />
      )}

      {mode === 'controlled' && (
        <ControlledCreationFlow
          onBack={() => setMode('pick')}
          onSubmit={handleCreateControlled}
          isSubmitting={isCreating}
          error={store.error}
          onDismissError={() => store.clearError()}
        />
      )}
    </div>
  );
});

export default DocumentCreatePage;
